import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthGate } from './AuthGate.tsx';
import { BILLING_QUERY_KEY_ROOT } from './data/billingApi.ts';
import { scenarioFocusOrderId, scenarioFocusQuoteId } from './data/model';
import { getFlowStateForWorkOrder, getQuoteDetailFromPilotState, getWorkOrderViewFromPilotState, snapshotFromMvpPilotState } from './data/mvpSelectors.ts';
import { useAppSettingsData } from './data/settingsApi.ts';
import { useAppWorkOrders } from './data/useAppData.ts';
import {
  addProbeCheckHttp,
  captureWorkOrderPhotosHttp,
  captureWorkOrderSignatureHttp,
  completeWorkOrderSyncRetryHttp,
  convertQuoteHttp,
  createWorkOrderInvoiceHttp,
  dispatchWorkOrderHttp,
  finalizeWorkOrderVoiceHttp,
  proposeFollowUpQuoteHttp,
  rejectWorkOrderReportHttp,
  retryWorkOrderSyncHttp,
  startWorkOrderHttp,
  submitWorkOrderReportHttp,
  validateWorkOrderReportHttp,
} from './data/httpApi.ts';
import { useDemoData } from './data/useDemoData';
import { APP_QUERY_KEY_ROOT } from './data/useAppData.ts';
import { MVP_QUERY_KEY_ROOT, useMvpPilotData } from './data/useMvpData';
import { updateMvpQuoteLine } from './data/mvpApi.ts';
import { reportRejectReason } from './data/model';
import type { DemoAction } from './data/actions';
import type {
  FlowState,
  ReportStatus,
  WorkOrder,
} from './data/types';
import { BillingPage } from './pages/BillingPage';
import { DispatchPage } from './pages/DispatchPage';
import { MobilePage } from './pages/MobilePage';
import { OverviewPage } from './pages/OverviewPage';
import { QuotesPage } from './pages/QuotesPage';
import { SettingsPage } from './pages/SettingsPage';
import { WorkOrdersPage } from './pages/WorkOrdersPage';
import {
  assetPath,
  displayNameLabel,
  formatHumanDateTimeLabel,
  formatRuntimeBadgeLabel,
  formatRuntimeBadgeTitle,
  roleLabel,
} from './utils';

const ROUTE_PATHS = {
  overview: '/overview',
  dispatch: '/dispatch',
  'work-orders': '/work-orders',
  quotes: '/quotes',
  billing: '/billing',
  settings: '/settings',
  mobile: '/mobile',
} as const;

type AppRoute = keyof typeof ROUTE_PATHS;
type MobileView = 'today' | 'job' | 'voice' | 'submit';

type RouteItem = {
  route: AppRoute;
  label: string;
  kicker: string;
};

const NAV_ITEMS: RouteItem[] = [
  { route: 'overview', label: "Vue d'ensemble", kicker: 'Cockpit direction' },
  { route: 'dispatch', label: 'Planification', kicker: 'Ordonnancement' },
  { route: 'work-orders', label: 'Interventions', kicker: 'Preuves terrain' },
  { route: 'quotes', label: 'Devis IA', kicker: 'Exploitation' },
  { route: 'billing', label: 'Facturation', kicker: 'Trésorerie & émissions' },
  { route: 'settings', label: 'Paramètres', kicker: 'Accès & configuration' },
  { route: 'mobile', label: 'Terrain mobile', kicker: 'Compagnon technicien' },
];

function routeIcon(route: AppRoute): string {
  switch (route) {
    case 'overview':
      return 'dashboard';
    case 'dispatch':
      return 'calendar_month';
    case 'quotes':
      return 'psychology';
    case 'work-orders':
      return 'handyman';
    case 'billing':
      return 'receipt_long';
    case 'settings':
      return 'settings';
    case 'mobile':
      return 'smartphone';
  }
}

const DISPATCH_WINDOW_PRESETS = ['10:45 - 12:00', '11:15 - 12:30', '13:30 - 14:45'] as const;

function getRouteFromPath(pathname: string): AppRoute {
  if (pathname.startsWith('/dispatch')) return 'dispatch';
  if (pathname.startsWith('/work-orders')) return 'work-orders';
  if (pathname.startsWith('/quotes')) return 'quotes';
  if (pathname.startsWith('/billing')) return 'billing';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/mobile')) return 'mobile';
  return 'overview';
}

const EMPTY_OPERATION_FLOW_STATE: FlowState = {
  quoteConverted: false,
  onSite: false,
  photosCaptured: false,
  voiceCaptured: false,
  signatureCaptured: false,
  reportStatus: 'draft',
  rejectionReason: null,
  syncStatus: 'pending',
  syncConflictReason: null,
  invoiceCreated: false,
};

function Shell(props: {
  currentUserName: string | null;
  currentUserRole: string | null;
  authBypassed: boolean;
  onLogout: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { snapshot, configuredMode, resolvedMode, runAction, refreshSnapshot } = useDemoData();
  const currentRoute = getRouteFromPath(location.pathname);
  const iconSrc = assetPath('tokenshift-icon.png');
  const apiEnabled = configuredMode === 'http' || resolvedMode === 'http';
  const { settings: shellSettings } = useAppSettingsData(apiEnabled);
  const preferredLandingRoute: AppRoute = (shellSettings?.preferences.landingRoute as AppRoute | undefined) ?? 'overview';

  const [selectedOrderId, setSelectedOrderId] = useState(scenarioFocusOrderId);
  const [selectedQuoteId, setSelectedQuoteId] = useState(scenarioFocusQuoteId);
  const [currentMobileView, setCurrentMobileView] = useState<MobileView>('today');
  const [dispatchOwnerName, setDispatchOwnerName] = useState('Nadia L.');
  const [dispatchScheduledWindow, setDispatchScheduledWindow] = useState('11:15 - 12:30');
  const [flashMessage, setFlashMessage] = useState('');
  const [overviewDate, setOverviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [activeTopbarPanel, setActiveTopbarPanel] = useState<'notifications' | 'history' | 'profile' | 'help' | null>(null);
  const [logoutPromptOpen, setLogoutPromptOpen] = useState(false);
  const mvpEnabled = configuredMode !== 'local';
  const { pilotState } = useMvpPilotData(mvpEnabled);
  const appWorkOrdersQuery = useAppWorkOrders({}, apiEnabled);
  const effectiveSnapshot = pilotState ? snapshotFromMvpPilotState(pilotState) : snapshot;
  const pilotWorkOrders = pilotState
    ? pilotState.workOrders
        .map((item) => getWorkOrderViewFromPilotState(pilotState, item.id))
        .filter((item): item is WorkOrder => Boolean(item))
    : effectiveSnapshot.workOrders;
  const selectedOrder =
    pilotState && pilotWorkOrders.length
      ? getWorkOrderViewFromPilotState(pilotState, selectedOrderId) ?? pilotWorkOrders[0]
      : effectiveSnapshot.workOrders.find((item) => item.id === selectedOrderId) ?? effectiveSnapshot.workOrders[0];
  const selectedOrderFlowState =
    pilotState && selectedOrder
      ? getFlowStateForWorkOrder(pilotState, selectedOrder.id)
      : effectiveSnapshot.flowState;
  const selectedQuoteDetail = pilotState ? getQuoteDetailFromPilotState(pilotState, selectedQuoteId) : null;
  const selectedQuoteHasProbeCheck = pilotState
    ? pilotState.quoteLines.some((item) => item.quoteId === selectedQuoteId && item.optionalFlag)
    : effectiveSnapshot.quoteHasProbeCheck;
  const selectedQuoteConvertedWorkOrderId = selectedQuoteDetail?.convertedWorkOrderId ?? null;
  const dispatchFocusOrder =
    pilotState && selectedQuoteConvertedWorkOrderId
      ? getWorkOrderViewFromPilotState(pilotState, selectedQuoteConvertedWorkOrderId)
      : null;
  const dispatchFlowState =
    pilotState && dispatchFocusOrder
      ? getFlowStateForWorkOrder(pilotState, dispatchFocusOrder.id)
      : { ...EMPTY_OPERATION_FLOW_STATE, quoteConverted: Boolean(selectedQuoteConvertedWorkOrderId) };
  const currentQuoteTitle =
    pilotState?.quotes.find((item) => item.id === selectedQuoteId)?.title ?? 'Diagnostic pompe à chaleur';
  const currentQuoteClient =
    pilotState?.quotes.find((item) => item.id === selectedQuoteId)?.clientName ?? 'Clinique Valmy';
  const currentQuoteSite =
    pilotState?.quotes.find((item) => item.id === selectedQuoteId)?.siteName ?? '4 avenue Pasteur, Dijon';
  const allowQuoteActions = !apiEnabled || props.authBypassed || ['admin', 'dispatch'].includes(props.currentUserRole ?? '');
  const allowDispatchActions = !apiEnabled || props.authBypassed || ['admin', 'dispatch'].includes(props.currentUserRole ?? '');
  const allowMobileActions = !apiEnabled || props.authBypassed || ['admin', 'technician'].includes(props.currentUserRole ?? '');
  const allowReviewActions = !apiEnabled || props.authBypassed || ['admin', 'backoffice'].includes(props.currentUserRole ?? '');
  const allowFollowUpActions =
    !apiEnabled || props.authBypassed || ['admin', 'dispatch', 'backoffice'].includes(props.currentUserRole ?? '');
  const currentNavItem = NAV_ITEMS.find((item) => item.route === currentRoute) ?? NAV_ITEMS[0];
  const dispatchTechnicianOptions = effectiveSnapshot.technicianLanes.map((lane) => lane.name);
  const dispatchWindowOptions = Array.from(
    new Set([dispatchFocusOrder?.window ?? '', selectedOrder?.window ?? '', ...DISPATCH_WINDOW_PRESETS].filter(Boolean)),
  );

  async function refreshOperationalQueries() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: MVP_QUERY_KEY_ROOT }),
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEY_ROOT }),
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY_ROOT }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: MVP_QUERY_KEY_ROOT, type: 'active' }),
      queryClient.refetchQueries({ queryKey: APP_QUERY_KEY_ROOT, type: 'active' }),
      queryClient.refetchQueries({ queryKey: BILLING_QUERY_KEY_ROOT, type: 'active' }),
    ]);
  }

  useEffect(() => {
    if (!pilotState?.quotes.length) return;
    if (!selectedQuoteId || !pilotState.quotes.some((quote) => quote.id === selectedQuoteId)) {
      setSelectedQuoteId(pilotState.quotes[0].id);
    }
  }, [pilotState, selectedQuoteId]);

  useEffect(() => {
    if (!pilotWorkOrders.length) return;
    if (!selectedOrderId || !pilotWorkOrders.some((order) => order.id === selectedOrderId)) {
      setSelectedOrderId(pilotWorkOrders[0].id);
    }
  }, [pilotWorkOrders, selectedOrderId]);

  useEffect(() => {
    if (!selectedQuoteConvertedWorkOrderId) return;
    setSelectedOrderId(selectedQuoteConvertedWorkOrderId);
  }, [selectedQuoteConvertedWorkOrderId]);

  useEffect(() => {
    if (selectedOrderFlowState.syncStatus !== 'retrying') return;
    const timeoutId = window.setTimeout(() => {
      const completeRetry = async () => {
        try {
          if (apiEnabled && selectedOrder) {
            await completeWorkOrderSyncRetryHttp(selectedOrder.id);
            await refreshOperationalQueries();
          } else {
            await runAction({ type: 'sync:complete-retry' });
          }
          setFlashMessage('Synchronisation alignée. Le rapport peut repartir.');
        } catch (error: unknown) {
          const detail = error instanceof Error ? error.message : "L'action demandée a échoué.";
          setFlashMessage(`Impossible d'exécuter l'action. ${detail}`);
        }
      };
      void completeRetry();
    }, 700);
    return () => window.clearTimeout(timeoutId);
  }, [apiEnabled, queryClient, runAction, selectedOrder, selectedOrderFlowState.syncStatus]);

  useEffect(() => {
    if (!flashMessage) return;
    const timeoutId = window.setTimeout(() => setFlashMessage(''), 4200);
    return () => window.clearTimeout(timeoutId);
  }, [flashMessage]);

  useEffect(() => {
    if (!selectedOrder) return;
    setDispatchOwnerName(selectedOrder.owner);
    setDispatchScheduledWindow(selectedOrder.window);
  }, [selectedOrder?.id, selectedOrder?.owner, selectedOrder?.window]);

  useEffect(() => {
    setActiveTopbarPanel(null);
  }, [location.pathname]);

  function goToRoute(route: AppRoute) {
    setActiveTopbarPanel(null);
    navigate(ROUTE_PATHS[route]);
  }

  function openWorkOrderById(workOrderId: string, route: AppRoute = 'work-orders') {
    setSelectedOrderId(workOrderId);
    goToRoute(route);
    setFlashMessage(`Ouverture du dossier ${workOrderId}.`);
  }

  function openWorkOrderByLabel(label: string, route: AppRoute = 'work-orders') {
    const candidate = pilotWorkOrders.find((item) => item.id === label || item.title === label || item.client === label);
    if (candidate) {
      openWorkOrderById(candidate.id, route);
      return;
    }
    const fallback = effectiveSnapshot.workOrders.find((item) => item.title === label || item.client === label);
    if (fallback) {
      openWorkOrderById(fallback.id, route);
      return;
    }
    setFlashMessage(`Aucun dossier ne correspond à « ${label} ».`);
  }

  async function requestLogout() {
    setActiveTopbarPanel(null);
    setLogoutPromptOpen(true);
  }

  async function executeAction(action: DemoAction): Promise<boolean> {
    try {
      await runAction(action);
      return true;
    } catch (error) {
      const detail = error instanceof Error ? error.message : "L'action demandée a échoué.";
      setFlashMessage(`Impossible d'exécuter l'action. ${detail}`);
      return false;
    }
  }

  async function executeHttpTask(task: () => Promise<unknown>): Promise<boolean> {
    try {
      await task();
      await refreshSnapshot();
      await refreshOperationalQueries();
      return true;
    } catch (error) {
      const detail = error instanceof Error ? error.message : "L'action demandée a échoué.";
      setFlashMessage(`Impossible d'exécuter l'action. ${detail}`);
      return false;
    }
  }

  async function handleAddProbeCheck() {
    if (!selectedQuoteId || selectedQuoteHasProbeCheck) return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => addProbeCheckHttp(selectedQuoteId)))) return;
    } else {
      if (selectedQuoteId !== scenarioFocusQuoteId) {
        setFlashMessage('Le mode local ne sait muter que le devis principal.');
        return;
      }
      if (!(await executeAction({ type: 'quote:add-probe-check' }))) return;
    }
    setFlashMessage('Option "contrôle sonde extérieure" ajoutée au devis.');
  }

  async function handleUpdateQuoteLine(
    quoteId: string,
    lineId: string,
    payload: { label: string; qtyLabel: string; quantity: number; unitCents: number },
  ) {
    if (!apiEnabled) {
      setFlashMessage('L’édition persistante des lignes de devis est disponible en mode connecté.');
      return;
    }
    if (!(await executeHttpTask(() => updateMvpQuoteLine(quoteId, lineId, payload)))) return;
    setFlashMessage('Ligne de devis enregistrée.');
  }

  async function handleConvertToWorkOrder() {
    if (!selectedQuoteId) return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => convertQuoteHttp(selectedQuoteId)))) return;
    } else {
      if (selectedQuoteId !== scenarioFocusQuoteId) {
        setFlashMessage('Le mode local ne sait convertir que le devis principal.');
        return;
      }
      if (!(await executeAction({ type: 'quote:convert' }))) return;
    }
    goToRoute('dispatch');
    setFlashMessage(`Le devis ${selectedQuoteId} est converti en intervention prête à être planifiée.`);
  }

  async function handleAssignDispatch() {
    if (!dispatchFocusOrder && !dispatchFlowState.quoteConverted) {
      setFlashMessage("Convertir ou sélectionner un dossier avant d'affecter le dispatch.");
      return;
    }
    if (apiEnabled) {
      if (
        !dispatchFocusOrder ||
        !(await executeHttpTask(() =>
          dispatchWorkOrderHttp(dispatchFocusOrder.id, {
            ownerName: dispatchOwnerName,
            scheduledWindow: dispatchScheduledWindow,
          }),
        ))
      ) {
        return;
      }
    } else {
      if (dispatchFocusOrder?.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait affecter que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'dispatch:assign', ownerName: dispatchOwnerName, scheduledWindow: dispatchScheduledWindow }))) {
        return;
      }
    }
    setFlashMessage(`Affectation confirmée pour ${dispatchOwnerName} · ${dispatchScheduledWindow}.`);
  }

  function handleFocusScenario() {
    setSelectedOrderId(scenarioFocusOrderId);
    setSelectedQuoteId(scenarioFocusQuoteId);
    goToRoute('work-orders');
    setFlashMessage('Le parcours principal est recentré sur WO-1837 · Clinique Valmy.');
  }

  async function handleStartJob() {
    if (!selectedOrder) return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => startWorkOrderHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait démarrer que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'job:start' }))) return;
    }
    setCurrentMobileView('job');
    goToRoute('mobile');
    setFlashMessage(`Le technicien démarre ${selectedOrder.title} depuis le mobile.`);
  }

  async function handleCapturePhotos() {
    if (!selectedOrder) return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => captureWorkOrderPhotosHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait capturer que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'job:capture-photos' }))) return;
    }
    setFlashMessage(`Preuves photo ajoutées au dossier ${selectedOrder.id}.`);
  }

  function handleOpenVoice() {
    setCurrentMobileView('voice');
    setFlashMessage('Le technicien ouvre le rapport vocal pour correction ou complément.');
  }

  async function handleFinalizeVoice() {
    if (!selectedOrder) return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => finalizeWorkOrderVoiceHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait finaliser que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'job:finalize-voice' }))) return;
    }
    setCurrentMobileView('submit');
    setFlashMessage('Le brouillon vocal est structuré localement.');
  }

  async function handleCaptureSignature() {
    if (!selectedOrder) return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => captureWorkOrderSignatureHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait signer que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'job:capture-signature' }))) return;
    }
    setCurrentMobileView('submit');
    setFlashMessage('La signature client est capturée. Le dossier est prêt à être envoyé.');
  }

  async function handleRetrySync() {
    if (!selectedOrder || selectedOrderFlowState.syncStatus !== 'conflict') return;
    if (apiEnabled) {
      if (!(await executeHttpTask(() => retryWorkOrderSyncHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait relancer que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'sync:set-retrying' }))) return;
    }
    setFlashMessage('Relance de synchronisation en cours...');
  }

  async function handleSubmitReport() {
    if (!selectedOrder) return;
    if (selectedOrderFlowState.syncStatus === 'pending' && !apiEnabled) {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait gérer que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'sync:mark-conflict' }))) return;
      setCurrentMobileView('submit');
      setFlashMessage('Conflit de synchronisation détecté. Résoudre le conflit avant envoi.');
      return;
    }

    if (selectedOrderFlowState.syncStatus === 'conflict' || selectedOrderFlowState.syncStatus === 'retrying') {
      setFlashMessage('Attendre la fin de la synchronisation avant envoi.');
      return;
    }

    const nextStatus: ReportStatus = selectedOrderFlowState.reportStatus === 'rejected' ? 'resubmitted' : 'submitted';
    if (apiEnabled) {
      if (!(await executeHttpTask(() => submitWorkOrderReportHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait soumettre que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'report:submit' }))) return;
    }
    goToRoute('work-orders');
    setFlashMessage(
      nextStatus === 'resubmitted'
        ? "Le rapport corrigé est re-soumis à l'exploitation."
        : 'Le rapport terrain est soumis et entre en file de validation.',
    );
  }

  async function handleValidateReport() {
    if (!selectedOrder) return;
    if (!['submitted', 'resubmitted'].includes(selectedOrderFlowState.reportStatus)) {
      setFlashMessage('Soumettre le rapport avant de le valider.');
      return;
    }
    if (selectedOrderFlowState.syncStatus !== 'synced') {
      setFlashMessage('La synchronisation doit être alignée avant validation.');
      return;
    }
    if (apiEnabled) {
      if (!(await executeHttpTask(() => validateWorkOrderReportHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait valider que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'report:validate' }))) return;
    }
    setFlashMessage('Le rapport est validé. La facture peut être créée.');
  }

  async function handleRejectReport() {
    if (!selectedOrder) return;
    if (!['submitted', 'resubmitted'].includes(selectedOrderFlowState.reportStatus)) {
      setFlashMessage("Le rapport doit d'abord être soumis.");
      return;
    }
    if (apiEnabled) {
      if (!(await executeHttpTask(() => rejectWorkOrderReportHttp(selectedOrder.id, { rejectionReason: reportRejectReason })))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait refuser que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'report:reject', rejectionReason: reportRejectReason }))) return;
    }
    setCurrentMobileView('submit');
    setFlashMessage("L'exploitation refuse le rapport et demande une reprise terrain.");
  }

  function handleOpenReworkMobile() {
    if (!selectedOrder) return;
    setCurrentMobileView('job');
    goToRoute('mobile');
    setFlashMessage(`La reprise terrain est ouverte sur ${selectedOrder.id}.`);
  }

  async function handleCreateInvoice() {
    if (!selectedOrder) return;
    if (selectedOrderFlowState.reportStatus !== 'validated' || selectedOrderFlowState.syncStatus !== 'synced') {
      setFlashMessage('Valider le rapport avant de créer la facture.');
      return;
    }
    if (apiEnabled) {
      if (!(await executeHttpTask(() => createWorkOrderInvoiceHttp(selectedOrder.id)))) return;
    } else {
      if (selectedOrder.id !== scenarioFocusOrderId) {
        setFlashMessage('Le mode local ne sait facturer que le scénario principal.');
        return;
      }
      if (!(await executeAction({ type: 'invoice:create' }))) return;
    }
    setFlashMessage(`Facture générée pour ${selectedOrder.id}.`);
  }

  async function handleProposeFollowUp() {
    if (!selectedOrder) return;
    if (!apiEnabled) {
      setFlashMessage("Le devis de suivi requiert l'API HTTP active.");
      return;
    }
    type FollowUpQuotePayload = { id: string };
    let createdQuoteId: string | null = null;
    const success = await executeHttpTask(async () => {
      const quote = await proposeFollowUpQuoteHttp<FollowUpQuotePayload>(selectedOrder.id);
      createdQuoteId = quote.id;
    });
    if (!success || !createdQuoteId) return;
    setSelectedQuoteId(createdQuoteId);
    goToRoute('quotes');
    setFlashMessage(`Le devis de suivi ${createdQuoteId} est prêt pour relecture.`);
  }

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar">
        <div className="app-shell__sidebar-brand">
          <img src={iconSrc} alt="" className="app-shell__brand-mark" />
          <div>
            <strong>Pilotage Service Terrain</strong>
            <span>Centre opérationnel</span>
          </div>
        </div>

        <nav className="app-shell__sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.route}
              to={ROUTE_PATHS[item.route]}
              className={({ isActive }) => `app-shell__sidebar-link${isActive ? ' is-active' : ''}`}
            >
              <span className="material-symbols-outlined app-shell__sidebar-icon">{routeIcon(item.route)}</span>
              <div>
                <strong>{item.label}</strong>
                <span>{item.kicker}</span>
              </div>
            </NavLink>
          ))}
        </nav>

        <div className="app-shell__sidebar-footer">
          <button type="button" className="app-shell__new-action" onClick={() => goToRoute('work-orders')}>
            <span className="material-symbols-outlined">add</span>
            <span>Nouvelle intervention</span>
          </button>
          <button type="button" className="app-shell__utility-link" onClick={() => setActiveTopbarPanel('help')}>
            <span className="material-symbols-outlined">help</span>
            <span>Centre d'aide</span>
          </button>
          {!props.authBypassed ? (
            <button type="button" className="app-shell__utility-link" onClick={() => void requestLogout()}>
              <span className="material-symbols-outlined">logout</span>
              <span>Déconnexion</span>
            </button>
          ) : null}
          <div className="app-shell__version-chip" title={formatRuntimeBadgeTitle()}>
            {formatRuntimeBadgeLabel()}
          </div>
        </div>
      </aside>

      <div className="app-shell__main">
        <header className="app-shell__topbar">
          <div className="app-shell__topbar-left">
            <div className="app-shell__topbar-title">
              <h1 style={{margin:0, fontSize:'1.25rem', fontWeight:600}}>{currentNavItem.label}</h1>
              <span className="app-shell__version-chip" title={formatRuntimeBadgeTitle()}>
                {formatRuntimeBadgeLabel()}
              </span>
            </div>
          </div>

          <div className="app-shell__topbar-right">
            <button
              type="button"
              className={`app-shell__icon-button${activeTopbarPanel === 'notifications' ? ' is-active' : ''}`}
              onClick={() => setActiveTopbarPanel((current) => (current === 'notifications' ? null : 'notifications'))}
            >
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <button
              type="button"
              className={`app-shell__icon-button${activeTopbarPanel === 'history' ? ' is-active' : ''}`}
              onClick={() => setActiveTopbarPanel((current) => (current === 'history' ? null : 'history'))}
            >
              <span className="material-symbols-outlined">history</span>
            </button>
            <button
              type="button"
              className={`app-shell__session app-shell__session-button${activeTopbarPanel === 'profile' ? ' is-active' : ''}`}
              onClick={() => setActiveTopbarPanel((current) => (current === 'profile' ? null : 'profile'))}
            >
              <div>
                <strong>{displayNameLabel(props.currentUserName) ?? 'Admin FST'}</strong>
                <span>{roleLabel(props.currentUserRole)}</span>
              </div>
              <span className="app-shell__avatar">{(props.currentUserName ?? 'AF').slice(0, 2).toUpperCase()}</span>
            </button>
          </div>
        </header>

        {activeTopbarPanel ? (
          <div className={`app-shell__popover app-shell__popover--${activeTopbarPanel}`}>
            {activeTopbarPanel === 'notifications' ? (
              <>
                <div className="app-shell__popover-head">
                  <strong>Notifications</strong>
                  <span>Signaux prioritaires du cockpit</span>
                </div>
                <div className="app-shell__popover-list">
                  <button type="button" className="app-shell__popover-item" onClick={() => openWorkOrderById(scenarioFocusOrderId)}>
                    <strong>{scenarioFocusOrderId} · validation en attente</strong>
                    <span>Ouvrir la fiche d'intervention pour valider le rapport et déclencher la facturation.</span>
                  </button>
                  <button type="button" className="app-shell__popover-item" onClick={() => goToRoute('billing')}>
                    <strong>Facturation prête</strong>
                    <span>3 dossiers attendent une émission ou une reprise de validation.</span>
                  </button>
                  <button type="button" className="app-shell__popover-item" onClick={() => goToRoute('dispatch')}>
                    <strong>Planification active</strong>
                    <span>Les dossiers non affectés nécessitent un arbitrage aujourd'hui.</span>
                  </button>
                </div>
              </>
            ) : null}
            {activeTopbarPanel === 'history' ? (
              <>
                <div className="app-shell__popover-head">
                  <strong>Historique récent</strong>
                  <span>Dernières étapes du flux</span>
                </div>
                <div className="app-shell__popover-list">
                  {selectedOrder.timeline.map((item) => (
                    <div key={`${item.at}-${item.label}`} className="app-shell__popover-item">
                      <strong>{formatHumanDateTimeLabel(`2026-04-01T${item.at}:00`)}</strong>
                      <span>{item.label}</span>
                    </div>
                  ))}
                  <div className="app-shell__popover-item">
                    <strong>{effectiveSnapshot.currentScenarioCaption}</strong>
                    <span>Le scénario actif reste centré sur {scenarioFocusOrderId} / {scenarioFocusQuoteId}.</span>
                  </div>
                </div>
              </>
            ) : null}
            {activeTopbarPanel === 'profile' ? (
              <>
                <div className="app-shell__popover-head">
                  <strong>{displayNameLabel(props.currentUserName) ?? 'Admin FST'}</strong>
                  <span>{roleLabel(props.currentUserRole)} · session active</span>
                </div>
                <div className="app-shell__popover-list">
                  <button type="button" className="app-shell__popover-item" onClick={() => goToRoute('settings')}>
                    <strong>Paramètres</strong>
                    <span>Ouvrir les préférences persistées et l'annuaire des utilisateurs.</span>
                  </button>
                  {!props.authBypassed ? (
                    <button type="button" className="app-shell__popover-item" onClick={() => void requestLogout()}>
                      <strong>Déconnexion</strong>
                      <span>Fermer la session courante avec confirmation.</span>
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
            {activeTopbarPanel === 'help' ? (
              <>
                <div className="app-shell__popover-head">
                  <strong>Centre d'aide</strong>
                  <span>Raccourcis de navigation</span>
                </div>
                <div className="app-shell__popover-list">
                  <button type="button" className="app-shell__popover-item" onClick={() => goToRoute('overview')}>
                    <strong>Vue d'ensemble</strong>
                    <span>Retourner au cockpit direction et aux indicateurs clés.</span>
                  </button>
                  <button type="button" className="app-shell__popover-item" onClick={() => goToRoute('work-orders')}>
                    <strong>Interventions</strong>
                    <span>Accéder à la validation, aux preuves terrain et à la facturation.</span>
                  </button>
                  <button type="button" className="app-shell__popover-item" onClick={() => goToRoute('settings')}>
                    <strong>Paramètres</strong>
                    <span>Modifier la route d'ouverture, le mode données et le mobile.</span>
                  </button>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        <main className="app-shell__canvas">
          <section className="app-shell__statusbar">
            <div>
              <span className="app-shell__status-kicker">{currentNavItem.kicker}</span>
              <strong>{currentNavItem.label}</strong>
            </div>
          </section>

          <div className={`flash-note${flashMessage ? ' is-visible' : ''}`}>{flashMessage}</div>

          <Routes>
          <Route index element={<Navigate to={ROUTE_PATHS[preferredLandingRoute]} replace />} />
          <Route
            path={ROUTE_PATHS.overview}
            element={
              <OverviewPage
                snapshot={effectiveSnapshot}
                selectedDate={overviewDate}
                onChangeDate={setOverviewDate}
                onRefresh={() => void refreshOperationalQueries()}
                onOpenRoute={(route) => goToRoute(route)}
                onOpenWorkOrderByTitle={openWorkOrderByLabel}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.dispatch}
            element={
              <DispatchPage
                lanes={effectiveSnapshot.technicianLanes}
                signals={effectiveSnapshot.dispatchSignals}
                unassignedJobs={effectiveSnapshot.unassignedJobs}
                backendWorkOrders={appWorkOrdersQuery.data ?? []}
                backendWorkOrdersLoading={appWorkOrdersQuery.isLoading}
                backendWorkOrdersEnabled={apiEnabled}
                focusOrder={dispatchFocusOrder}
                flowState={dispatchFlowState}
                dispatchOwnerName={dispatchOwnerName}
                dispatchScheduledWindow={dispatchScheduledWindow}
                dispatchTechnicianOptions={dispatchTechnicianOptions}
                dispatchWindowOptions={dispatchWindowOptions}
                onChangeDispatchOwnerName={setDispatchOwnerName}
                onChangeDispatchScheduledWindow={setDispatchScheduledWindow}
                onConfirmDispatch={handleAssignDispatch}
                onOpenQuote={handleConvertToWorkOrder}
                onOpenWorkOrderByTitle={openWorkOrderByLabel}
                canManageDispatch={allowDispatchActions}
              />
            }
          />
          <Route
            path={ROUTE_PATHS['work-orders']}
            element={
              <WorkOrdersPage
                orders={pilotWorkOrders}
                selectedOrderId={selectedOrder?.id ?? scenarioFocusOrderId}
                flowState={selectedOrderFlowState}
                onSelectOrder={setSelectedOrderId}
                onFocusScenario={handleFocusScenario}
                onReturnDispatch={() => goToRoute('dispatch')}
                onValidateReport={handleValidateReport}
                onRejectReport={handleRejectReport}
                onOpenReworkMobile={handleOpenReworkMobile}
                onCreateInvoice={handleCreateInvoice}
                onProposeFollowUp={handleProposeFollowUp}
                onOpenWorkOrderById={(workOrderId) => openWorkOrderById(workOrderId)}
                appWorkspaceEnabled={apiEnabled}
                currentUserRole={props.currentUserRole}
                canReviewActions={allowReviewActions}
                canOpenReworkMobile={allowMobileActions}
                canCreateInvoice={allowReviewActions}
                canProposeFollowUp={allowFollowUpActions}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.quotes}
            element={
              <QuotesPage
                quoteIds={pilotState?.quotes.map((quote) => quote.id) ?? [scenarioFocusQuoteId]}
                selectedQuoteId={selectedQuoteId}
                quoteTitle={currentQuoteTitle}
                quoteClient={currentQuoteClient}
                quoteSite={currentQuoteSite}
                activeQuoteLines={selectedQuoteDetail?.lines ?? effectiveSnapshot.activeQuoteLines}
                subtotalCents={selectedQuoteDetail?.subtotalCents ?? effectiveSnapshot.subtotalCents}
                vatCents={selectedQuoteDetail?.vatCents ?? effectiveSnapshot.vatCents}
                totalCents={selectedQuoteDetail?.totalCents ?? effectiveSnapshot.totalCents}
                quoteHasProbeCheck={selectedQuoteHasProbeCheck}
                quoteConverted={Boolean(selectedQuoteConvertedWorkOrderId)}
                quoteSignals={effectiveSnapshot.quoteSignals}
                onSelectQuote={setSelectedQuoteId}
                onAddProbeCheck={handleAddProbeCheck}
                onConvertToWorkOrder={handleConvertToWorkOrder}
                onSaveQuoteLine={handleUpdateQuoteLine}
                canManageQuotes={allowQuoteActions}
                quoteLineEditingEnabled={apiEnabled}
                onOpenQuoteList={() => goToRoute('quotes')}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.billing}
            element={
              <BillingPage
                snapshot={effectiveSnapshot}
                apiEnabled={apiEnabled}
                onOpenWorkOrderById={(workOrderId) => openWorkOrderById(workOrderId)}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.settings}
            element={
              <SettingsPage
                apiEnabled={apiEnabled}
                authBypassed={props.authBypassed}
                currentUserName={props.currentUserName}
                currentUserRole={props.currentUserRole}
                configuredMode={configuredMode}
                resolvedMode={resolvedMode}
                onLogout={requestLogout}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.mobile}
            element={
              <MobilePage
                iconSrc={iconSrc}
                currentMobileView={currentMobileView}
                activeOrder={selectedOrder ?? null}
                flowState={selectedOrderFlowState}
                mobileToday={effectiveSnapshot.mobileToday}
                syncItems={effectiveSnapshot.mobileSyncItems}
                onSelectView={setCurrentMobileView}
                onStartJob={handleStartJob}
                onCapturePhotos={handleCapturePhotos}
                onOpenVoice={handleOpenVoice}
                onFinalizeVoice={handleFinalizeVoice}
                onCaptureSignature={handleCaptureSignature}
                onRetrySync={handleRetrySync}
                onSubmitReport={handleSubmitReport}
                onOpenReworkMobile={handleOpenReworkMobile}
                canOperateMobile={allowMobileActions}
              />
            }
          />
          <Route path="*" element={<Navigate to={ROUTE_PATHS[currentRoute]} replace />} />
          </Routes>

          {logoutPromptOpen ? (
            <div className="app-shell__modal-backdrop">
              <div className="app-shell__modal">
                <strong>Fermer la session ?</strong>
                <p>Vous allez revenir à l’écran de connexion. Les sélections courantes ne seront plus accessibles tant que la session n’est pas réouverte.</p>
                <div className="action-row">
                  <button type="button" className="secondary-button" onClick={() => setLogoutPromptOpen(false)}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="primary-button"
                    onClick={async () => {
                      setLogoutPromptOpen(false);
                      await props.onLogout();
                    }}
                  >
                    Fermer la session
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </main>
      </div>

      <nav className="app-shell__mobile-nav">
        <div className="app-shell__mobile-nav-items">
          {([
            { route: 'overview' as AppRoute, icon: 'dashboard', label: "Vue d'ensemble" },
            { route: 'dispatch' as AppRoute, icon: 'calendar_month', label: 'Planification' },
            { route: 'work-orders' as AppRoute, icon: 'handyman', label: 'Interventions' },
            { route: 'quotes' as AppRoute, icon: 'psychology', label: 'Devis' },
            { route: 'billing' as AppRoute, icon: 'receipt_long', label: 'Facturation' },
          ]).map((item) => (
            <button
              key={item.route}
              type="button"
              className={`app-shell__mobile-nav-item${currentRoute === item.route ? ' is-active' : ''}`}
              onClick={() => goToRoute(item.route)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function App() {
  return (
    <AuthGate>
      {(auth) => <Shell {...auth} />}
    </AuthGate>
  );
}
